import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CreateGeofenceForm } from '@geofences/create-geofence-form/create-geofence-form';
import { DEFAULT_PAGINATION_CONFIG, GeofenceKind, GeofenceV1Response, GroupedGeofence } from '@geofences/geofence';
import { GeofenceCard } from '@geofences/geofence-card/geofence-card';
import { GeofenceLoading } from '@geofences/geofence-loading/geofence-loading';
import { ButtonModule } from 'primeng/button';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';
import { firstValueFrom } from 'rxjs';

import { PageHeader } from '@core/components/page-header/page-header';
import { Pagination } from '@core/components/pagination/pagination';
import { GeofenceService } from '@core/services/geofence';

@Component({
  selector: 'app-geofences',
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    ToolbarModule,
    TagModule,
    SkeletonModule,
    TooltipModule,
    PageHeader,
    GeofenceLoading,
    InputIconModule,
    IconFieldModule,
    GeofenceCard,
    Pagination
  ],
  providers: [DialogService],
  templateUrl: './geofences-list.html',
  styleUrl: './geofences-list.scss'
})
export class GeofencesList {
  private readonly geofenceApis = inject(GeofenceService);
  private readonly router = inject(Router);
  private readonly dialogService = inject(DialogService);

  protected readonly geofences = this.geofenceApis.geofences;
  protected readonly paginationMeta = this.geofenceApis.paginationMeta;

  private readonly dialogRef = signal<DynamicDialogRef | null>(null);
  protected readonly searchTerm = signal<string>('');
  protected readonly loading = signal<boolean>(false);
  protected readonly error = signal<boolean>(false);
  protected readonly currentPage = signal<number>(0);
  protected readonly pageSize = signal<number>(DEFAULT_PAGINATION_CONFIG.defaultPageSize);
  protected readonly totalRecords = computed(() => this.paginationMeta().totalRecords);

  readonly groupedGeofences = computed(() => this.groupByName(this.geofences()));

  private groupByName(geofences: GeofenceV1Response[]): GroupedGeofence[] {
    const groups = new Map<string, GroupedGeofence>();

    for (const gf of geofences) {
      const group = groups.get(gf.name) ?? {
        name: gf.name,
        createdDate: gf.createdDate,
        updatedDate: gf.updatedDate
      };

      switch (gf.geofenceKindId) {
        case GeofenceKind.Hub:
          group.hub = gf;
          break;
        case GeofenceKind.Delivery:
          group.delivery = gf;
          break;
        case GeofenceKind.Pickup:
          group.pickup = gf;
          break;
      }

      if (gf.createdDate < group.createdDate) group.createdDate = gf.createdDate;
      if (gf.updatedDate > group.updatedDate) group.updatedDate = gf.updatedDate;
      groups.set(gf.name, group);
    }

    return [...groups.values()];
  }

  readonly filteredGroupedGeofences = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    if (!term) return this.groupedGeofences();
    return this.groupedGeofences().filter((group) => group.name.toLowerCase().includes(term));
  });

  ngOnInit() {
    this.loadData();
  }

  async loadData(pageNum = 1, pageSize = this.pageSize()) {
    this.loading.set(true);
    this.error.set(false);

    try {
      await firstValueFrom(this.geofenceApis.getAll(pageNum, pageSize));
    } catch (error) {
      console.error('Failed to load geofences:', error);
      this.error.set(true);
    } finally {
      this.loading.set(false);
    }
  }

  onPageChange(page: number) {
    this.currentPage.set(page);
    this.loadData(page + 1, this.pageSize());
  }

  onPageSizeChange(size: number) {
    this.pageSize.set(size);
    this.currentPage.set(0);
    this.loadData(1, size);
  }

  onSearchInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm.set(value);
  }

  navigateToPath(path: string[]) {
    this.router.navigate(path);
  }

  displayCreateGeofenceDialog() {
    const dialogRef = this.dialogService.open(CreateGeofenceForm, {
      modal: true,
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      },
      data: {
        title: 'Create Geofence',
        subTitle: 'Setup a new geofence for a hub.'
      }
    });
    dialogRef?.onClose.subscribe((geofenceId) => {
      if (geofenceId) {
        this.navigateToPath(['geofences', 'edit', geofenceId]);
      } else {
        this.loadData(this.currentPage() + 1, this.pageSize());
      }
    });
    this.dialogRef.set(dialogRef);
  }
}
